import * as Constants from "@/shared/constants";
import {
	calculateDurationBetweenTimes,
	formatDuration,
	formatTime,
	getISOTime,
	parseDuration,
	parseTimeInput
} from "@/shared/utils/datefunctions";
import {TargetWorkingTimeType} from "@/shared/timrTypes";
import {WorkScheduleWeek} from "@/shared/timr";

type NormalWorkingTimeType =
	| 'normalWorkingTime1Start'
	| 'normalWorkingTime1End'
	| 'normalWorkingTime2Start'
	| 'normalWorkingTime2End';

type InputDataType = NormalWorkingTimeType | 'dailyWorkingHours';

export class WorkScheduleWeekComponent extends HTMLElement {

	public static readonly EVENT_UPDATED = "workScheduleWeek.updated";
	public static readonly EVENT_CHANGED = "workScheduleWeek.changed";

	private readonly template = `
		<div class="form-group" data-normal-working-time>
			<div class="col-md-3 text-right">
				<div class="labelContainer weekdayMargin">
					<label id="normalWorkingTime1StartLabel">
						${messages["workScheduleModel.normalWorkingTime.start"]}
					</label>
				</div>
				<div class="labelContainer">
					<label id="normalWorkingTime1EndLabel">
						${messages["workScheduleModel.normalWorkingTime.end"]}
					</label>
				</div>
			</div>

			<div class="col-md-9 equally-spaced-flex-divs">
				${Constants.WEEKDAYS.map((day) => {
		return `
						<div class="form-group text-center no-margin-bottom">
							<label class="text-grey">
								${messages[`workScheduleModel.${day.toLowerCase()}.short`]}
							</label>
							<input name="workScheduleWeek.normalWorkingTime1Start${day}" type="text"
								   class="form-control text-center no-padding mb-5" size="6" maxlength="5" data-day="${day}" data-type="normalWorkingTime1Start" />
							<input name="workScheduleWeek.normalWorkingTime1End${day}" type="text"
								   class="form-control text-center no-padding" size="6" maxlength="5" data-day="${day}" data-type="normalWorkingTime1End" />
						</div>
					`;
	}).join("")}
			</div>
		</div>

		<div class="form-group" data-normal-working-time>
			<div class="col-md-3 text-right">
				<div class="labelContainer weekdayMargin">
					<label id="normalWorkingTime2StartLabel">
						${messages["workScheduleModel.normalWorkingTime.start"]}
					</label>
				</div>
				<div class="labelContainer">
					<label id="normalWorkingTime2EndLabel">
						${messages["workScheduleModel.normalWorkingTime.end"]}
					</label>
				</div>
			</div>

			<div class="col-md-9 equally-spaced-flex-divs">
				${Constants.WEEKDAYS.map((day) => {
		return `
						<div class="form-group text-center no-margin-bottom">
							<label class="text-grey">
								${messages[`workScheduleModel.${day.toLowerCase()}.short`]}
							</label>
							<input name="workScheduleWeek.normalWorkingTime2Start${day}" type="text"
								   class="form-control text-center no-padding mb-5" size="6" maxlength="5" data-day="${day}" data-type="normalWorkingTime2Start" />
							<input name="workScheduleWeek.normalWorkingTime2End${day}" type="text"
								   class="form-control text-center no-padding" size="6" maxlength="5" data-day="${day}" data-type="normalWorkingTime2End" />
						</div>
					`;
	}).join("")}
			</div>
		</div>

		<div class="form-group" data-daily-working-hours>
			<div class="col-md-3">
				<div class="form-group text-right">
					<label class="control-label" id="targetHoursLabel">
						${messages["workScheduleModel.targetHours"]}
					</label>
				</div>
			</div>

			<div class="col-md-9 equally-spaced-flex-divs">
				${Constants.WEEKDAYS.map((day) => {
		return `
						<div class="form-group text-center no-margin-bottom">
							<label class="text-grey">
								${messages[`workScheduleModel.${day.toLowerCase()}.short`]}
							</label>
							<input type="text" class="form-control text-center" size="6" maxlength="5" data-day="${day}" data-type="dailyWorkingHours" />
						</div>
					`;
	}).join("")}
			</div>
		</div>
	`;

	private _namePrefix: string;
	private _targetWorkingTimeType: TargetWorkingTimeType = TargetWorkingTimeType.DAILY_TARGET_HOURS;
	private _workScheduleWeek: WorkScheduleWeek = {
		workDurationMonday: 0,
		workDurationTuesday: 0,
		workDurationWednesday: 0,
		workDurationThursday: 0,
		workDurationFriday: 0,
		workDurationSaturday: 0,
		workDurationSunday: 0
	};

	public set namePrefix(prefix: string) {
		this._namePrefix = prefix;
		this.setNamePrefix();
	}

	public set targetWorkingTimeType(value: TargetWorkingTimeType) {
		this._targetWorkingTimeType = value;
		this.render();
	}

	public get workScheduleWeek(): WorkScheduleWeek {
		return this._workScheduleWeek;
	}

	public set workScheduleWeek(value: WorkScheduleWeek) {
		this._workScheduleWeek = value;
		this.render();
	}

	public get totalWorkDuration(): number {
		return Constants.WEEKDAYS.reduce((acc, day) => {
			return acc + (this._workScheduleWeek[`workDuration${day}`] || 0);
		}, 0);
	}

	public connectedCallback(): void {
		this.innerHTML = this.template;
		this.setNamePrefix();
		this.initListeners();
		this.render();
	}

	private initListeners(): void {
		for (const input of this.querySelectorAll("input[data-type]")) {
			input.addEventListener("change", (e) => {
				const target = e.target as HTMLInputElement;
				const day = target.dataset.day as Constants.Weekday;
				const type = target.dataset.type as InputDataType;

				if (this.isNormalWorkingTimeType(type)) {
					const time = parseTimeInput(target.value);
					this._workScheduleWeek[`${type}${day}`] = getISOTime(time);
					this.calculateTargetHoursSum(day);
				} else if (type === "dailyWorkingHours") {
					this._workScheduleWeek[`workDuration${day}`] = parseDuration(target.value, true) * 60000;
				}

				this.dispatchEvent(new Event(WorkScheduleWeekComponent.EVENT_CHANGED));

				this.render();
			});
		}
	}

	private render(): void {
		// Update visibility based on targetWorkingTimeType
		const isDailyTargetHours = this._targetWorkingTimeType === TargetWorkingTimeType.DAILY_TARGET_HOURS;
		for (const e of this.querySelectorAll('[data-normal-working-time]')) {
			(e as HTMLElement).style.display = isDailyTargetHours ? 'none' : 'block';
		}
		for (const e of this.querySelectorAll('[data-daily-working-hours] input')) {
			(e as HTMLInputElement).disabled = !isDailyTargetHours;
		}

		// Update input values from state
		for (const input of this.querySelectorAll("input[data-type]")) {
			const target = input as HTMLInputElement;
			const day = target.dataset.day as Constants.Weekday;
			const type = target.dataset.type as InputDataType;

			if (this.isNormalWorkingTimeType(type)) {
				const time = this._workScheduleWeek[`${type}${day}`];
				target.value = formatTime(parseTimeInput(time));
			} else if (type === "dailyWorkingHours") {
				const duration = this._workScheduleWeek[`workDuration${day}`];
				target.value = formatDuration(duration);
			}
		}

		this.dispatchEvent(new Event(WorkScheduleWeekComponent.EVENT_UPDATED));
	}

	private isNormalWorkingTimeType(type: InputDataType): type is NormalWorkingTimeType {
		return type.startsWith("normalWorkingTime");
	}

	/**
	 * Calculates the sum of the time spans of the two configured hours of a weekday
	 * @private
	 */
	private calculateTargetHoursSum(weekday: Constants.Weekday): void {
		const normalWorkingTime1Start = this._workScheduleWeek[`normalWorkingTime1Start${weekday}`];
		const normalWorkingTime1End = this._workScheduleWeek[`normalWorkingTime1End${weekday}`];
		const normalWorkingTime2Start = this._workScheduleWeek[`normalWorkingTime2Start${weekday}`];
		const normalWorkingTime2End = this._workScheduleWeek[`normalWorkingTime2End${weekday}`];

		if (normalWorkingTime1Start > normalWorkingTime1End || normalWorkingTime2Start > normalWorkingTime2End) {
			return;
		}
		const duration1 = calculateDurationBetweenTimes(normalWorkingTime1Start, normalWorkingTime1End);
		const duration2 = calculateDurationBetweenTimes(normalWorkingTime2Start, normalWorkingTime2End);
		this._workScheduleWeek[`workDuration${weekday}`] = duration1.add(duration2).total('milliseconds');
	}

	private setNamePrefix() {
		if (this._namePrefix) {
			for (const input of this.querySelectorAll("input")) {
				const name = input.name;
				input.name = `${this._namePrefix}.workScheduleWeek.${name.substring(name.lastIndexOf(".") + 1)}`;
			}
		}
	}
}
